import type { PostTablesLabels200DataItem } from '@ipfabric/api-client/client/schemas/postTablesLabels200DataItem';
import type {
  LabelTargetType,
  SnapshotId,
  TableRequestBody,
} from '@ipfabric/utils';
import {
  assignLabels,
  deleteAssignedLabel,
} from 'pages/Settings/DiscoverySnapshots/DiscoveryDataEnrichment/AssignedLabels/assignedLabelsApi';
import { useEffect, useMemo, useState } from 'react';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useApi } from 'utils/request/useApi';
import {
  type LabelAssignment,
  getLabelsTargetFilter,
  mapLabelAssignmentRow,
} from './labelAssignments';

type UseLabelAssignmentsParams = {
  onSaved: () => void;
  snapshotId: SnapshotId;
  targetId: string;
  targetType: LabelTargetType;
};

const splitAssignments = (rows: PostTablesLabels200DataItem[]) => {
  const manual: LabelAssignment[] = [];
  const readOnly: LabelAssignment[] = [];

  for (const row of rows) {
    const assignment = mapLabelAssignmentRow(row);
    if (!assignment) {
      continue;
    }
    if (assignment.source === 'manual') {
      manual.push(assignment);
    } else {
      readOnly.push(assignment);
    }
  }

  return { manual, readOnly };
};

export const useLabelAssignments = ({
  onSaved,
  snapshotId,
  targetId,
  targetType,
}: UseLabelAssignmentsParams) => {
  const { request } = useApi();
  const { showNotification } = useNotifications();
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialManual, setInitialManual] = useState<LabelAssignment[]>([]);
  const [draftManual, setDraftManual] = useState<LabelAssignment[]>([]);
  const [readOnlyLabels, setReadOnlyLabels] = useState<LabelAssignment[]>([]);

  useEffect(() => {
    let isCurrentRequest = true;
    setLoading(true);
    setInitialManual([]);
    setDraftManual([]);
    setReadOnlyLabels([]);

    const loadAssignments = async () => {
      try {
        const response = await request<
          { data: PostTablesLabels200DataItem[] },
          TableRequestBody<PostTablesLabels200DataItem>
        >({
          url: '/tables/labels',
          method: 'POST',
          data: {
            columns: [
              'labelId',
              'label',
              'name',
              'value',
              'assignment',
              'ruleId',
              'assignedBy',
              'rootAssignment',
              'rootId',
              'rootRuleId',
            ],
            filters: getLabelsTargetFilter(targetType, targetId),
            snapshot: snapshotId,
          },
        });

        const { manual, readOnly } = splitAssignments(response.data);

        if (isCurrentRequest) {
          setInitialManual(manual);
          setDraftManual(manual);
          setReadOnlyLabels(readOnly);
        }
      } finally {
        if (isCurrentRequest) {
          setLoading(false);
        }
      }
    };
    void loadAssignments();

    return () => {
      isCurrentRequest = false;
    };
  }, [snapshotId, targetId, targetType]);

  const draftManualLabels = useMemo(
    () => draftManual.map((item) => item.label),
    [draftManual],
  );

  const applyPickerDraft = (pickerDraftLabels: string[]) => {
    const existingByLabel = new Map(
      draftManual.map((item) => [item.label, item]),
    );
    setDraftManual(
      pickerDraftLabels.map((label) => {
        const existing = existingByLabel.get(label);
        if (existing) {
          return existing;
        }
        return {
          label,
          labelId: `new:${label}`,
          linkedToRule: false,
          source: 'manual' as const,
        };
      }),
    );
  };

  const removeManualLabel = (label: string) => {
    setDraftManual((current) => current.filter((item) => item.label !== label));
  };

  const save = async (reflectToRules: boolean) => {
    setIsSubmitting(true);
    try {
      const initialByLabel = new Map(
        initialManual.map((item) => [item.label, item]),
      );
      const draftByLabel = new Map(
        draftManual.map((item) => [item.label, item]),
      );

      const toAdd = draftManual
        .filter((item) => !initialByLabel.has(item.label))
        .map((item) => item.label);
      const toDelete = initialManual.filter(
        (item) => !draftByLabel.has(item.label),
      );

      if (toAdd.length > 0) {
        await assignLabels(
          snapshotId,
          toAdd,
          [{ type: targetType, targetId }],
          false,
          reflectToRules,
        );
      }

      for (const item of toDelete) {
        await deleteAssignedLabel(
          snapshotId,
          item.labelId,
          reflectToRules && item.linkedToRule ? true : undefined,
        );
      }

      showNotification({
        type: 'success',
        message: 'Labels saved',
      });
      onSaved();
    } catch {
      showNotification({
        type: 'danger',
        message: 'Failed to save labels',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    applyPickerDraft,
    draftManual,
    draftManualLabels,
    isSubmitting,
    loading,
    readOnlyLabels,
    removeManualLabel,
    save,
  };
};
